/**
 * @module Page-IT-Security-Training
 */

import { BANNER_TYPE } from "../../../globals/constants";
import { useEffect } from "react";
import { loadScript } from "../../../globals/common-functions";
import banner from "../../../globals/banner.json";
import AvertImage from "../../../globals/elements/avertimg";
import SectionInnerPageBanner from "../../sections/common/banner";

function ITSecurityTrainingPage() {

    useEffect(()=>{
        loadScript("js/custom.js");
    })

    return (

        <div className="av-sv2-wrap">
            <SectionInnerPageBanner _data={banner.service_detail} _type={BANNER_TYPE.COMPACT} />

            {/* Hero */}
            <div className="section-full p-t120 p-b0 site-bg-white">
                <div className="container">
                    <div className="av-hero-v2">
                        <div className="row align-items-center">
                            <div className="col-lg-7">
                                <div className="av-hero-eyebrow">
                                    <i className="feather feather-shield"></i>
                                    Security & Training
                                </div>
                                <h1>IT Security & Training</h1>
                                <p className="av-hero-lead">
                                    Cyber threats are real, but protecting your business doesn't have to be complicated.
                                    We secure your systems and train your team so everyone knows how to stay safe online.
                                </p>
                            </div>
                            <div className="col-lg-5">
                                <div className="av-hero-visual">
                                    <AvertImage src="images/services/it-security-training.jpg" alt="IT Security & Training" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Two Services */}
            <div className="av-section-v2 av-bg-slate">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-6 m-b30">
                            <div className="av-card-v2">
                                <div className="av-card-icon">
                                    <i className="feather feather-lock"></i>
                                </div>
                                <h3>IT Security Solutions</h3>
                                <p>We put the right protections in place to keep hackers out and your data safe — without making technology harder for your team to use.</p>
                                <ul>
                                    <li>Security assessments and audits</li>
                                    <li>Firewall and antivirus configuration</li>
                                    <li>Data backup and recovery plans</li>
                                    <li>Password policy setup</li>
                                    <li>Network security monitoring</li>
                                </ul>
                            </div>
                        </div>
                        <div className="col-lg-6 m-b30">
                            <div className="av-card-v2">
                                <div className="av-card-icon">
                                    <i className="feather feather-users"></i>
                                </div>
                                <h3>Security Training</h3>
                                <p>Your team is your first line of defence. When they know what to watch out for, they stop most attacks before they start.</p>
                                <ul>
                                    <li>Phishing awareness training</li>
                                    <li>Password security workshops</li>
                                    <li>Safe browsing practices</li>
                                    <li>Social engineering awareness</li>
                                    <li>Incident response procedures</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Threats - Dark Panel */}
            <div className="av-section-v2">
                <div className="container">
                    <div className="av-panel-dark">
                        <h3>Threats we help you avoid</h3>
                        <p>These are the most common attacks targeting businesses like yours. The good news — they're all preventable with the right measures in place.</p>
                        <div className="av-pd-grid">
                            <div className="av-pd-item">
                                <i className="feather feather-mail"></i>
                                <h5>Phishing emails</h5>
                                <p>Fake messages designed to trick your team into handing over passwords or sensitive information.</p>
                            </div>
                            <div className="av-pd-item">
                                <i className="feather feather-alert-triangle"></i>
                                <h5>Malware & ransomware</h5>
                                <p>Malicious software that can lock your files or steal data — often spread through dodgy downloads or links.</p>
                            </div>
                            <div className="av-pd-item">
                                <i className="feather feather-key"></i>
                                <h5>Weak passwords</h5>
                                <p>Simple or reused passwords are the easiest way in for attackers. It's the most common vulnerability we see.</p>
                            </div>
                            <div className="av-pd-item">
                                <i className="feather feather-hard-drive"></i>
                                <h5>Data loss</h5>
                                <p>Hardware failures, accidents, or attacks can wipe out important files — unless you have proper backups in place.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Security Checklist + Stats */}
            <div className="av-section-v2 av-bg-slate">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-7 m-b30">
                            <div className="av-panel-accent" style={{height: '100%'}}>
                                <h3>Our security checklist</h3>
                                <p>Here's what we cover when we secure your business. It's thorough, but we handle it all.</p>
                                <ul className="av-checklist-v2">
                                    <li><i className="feather feather-check"></i> Review current security setup and find vulnerabilities</li>
                                    <li><i className="feather feather-check"></i> Install and configure firewalls and security software</li>
                                    <li><i className="feather feather-check"></i> Set up automated backups for critical data</li>
                                    <li><i className="feather feather-check"></i> Create strong password policies with two-factor authentication</li>
                                    <li><i className="feather feather-check"></i> Train your staff on security best practices</li>
                                    <li><i className="feather feather-check"></i> Establish incident response procedures</li>
                                </ul>
                            </div>
                        </div>
                        <div className="col-lg-5 m-b30">
                            <div className="av-callout" style={{height: '100%'}}>
                                <h4>Why this matters</h4>
                                <p style={{fontSize: '32px', fontWeight: 800, marginBottom: '8px', color: '#fff', lineHeight: 1.2}}>95%</p>
                                <p style={{marginBottom: '24px'}}>of successful cyber attacks are caused by human error — which is exactly why training is so important.</p>
                                <p style={{fontSize: '32px', fontWeight: 800, marginBottom: '8px', color: '#fff', lineHeight: 1.2}}>287 days</p>
                                <p style={{marginBottom: '24px'}}>is the average time to identify and contain a breach. Early detection changes everything.</p>
                                <a href="/contact-us" className="av-callout-btn">
                                    Get a Security Assessment <i className="feather feather-arrow-right"></i>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Training Programs */}
            <div className="av-section-v2">
                <div className="container">
                    <div className="av-sh">
                        <div className="av-sh-label">Training</div>
                        <h2>Security Training Programs</h2>
                        <p>Practical sessions that give your team the knowledge they need to keep your business safe.</p>
                    </div>

                    <div className="row">
                        <div className="col-lg-4 col-md-6 m-b30">
                            <div className="av-card-v2">
                                <div className="av-card-icon">
                                    <i className="feather feather-book"></i>
                                </div>
                                <h3>Security Awareness</h3>
                                <p><strong>2 hours</strong> — The essentials for every team member. Covers password safety, spotting phishing emails, and safe browsing habits.</p>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6 m-b30">
                            <div className="av-card-v2">
                                <div className="av-card-icon">
                                    <i className="feather feather-crosshair"></i>
                                </div>
                                <h3>Threat Detection</h3>
                                <p><strong>4 hours</strong> — For IT staff and managers. Recognising sophisticated attacks, social engineering tactics, and advanced persistent threats.</p>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6 m-b30">
                            <div className="av-card-v2">
                                <div className="av-card-icon">
                                    <i className="feather feather-alert-circle"></i>
                                </div>
                                <h3>Incident Response</h3>
                                <p><strong>3 hours</strong> — What to do when something goes wrong. Immediate response steps, communication protocols, and damage limitation.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* CTA */}
            <div className="av-section-v2 av-bg-slate">
                <div className="container">
                    <div className="av-cta-v2">
                        <h3>Want to know where your security stands?</h3>
                        <p>We'll take a look at your current setup and give you an honest assessment — along with practical steps to fix any gaps.</p>
                        <div className="av-cta-actions">
                            <a href="/contact-us" className="av-btn-white">Book an Assessment</a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
export default ITSecurityTrainingPage;
